import { create } from 'zustand'

/**
 * Authentication state.
 *
 * Auth itself lives in an httpOnly cookie set by the backend — JavaScript
 * cannot read it (XSS protection). This store only mirrors a *presence* flag
 * (`isAuthenticated`) plus user metadata.
 *
 * Source of `User`: `POST /api/auth/login` response (decisions-log D-03 —
 * Go has no `/api/auth/me`, so the frontend reconstructs the user object
 * from login response and persists nothing here). On F5 the root-loader
 * re-bootstraps via a lightweight authenticated probe.
 *
 * NOT persisted on purpose: if the backend revokes the session server-side
 * (e.g. admin force-logout) we do NOT want a stale "user is logged in"
 * shape lingering in localStorage.
 */
export type User = {
  username: string
  realUsername: string
  role: 'admin' | 'user'
  userCode: string
  activeWorkspaceId: number | null
  workspaceName: string | null
}

export type AuthState = {
  user: User | null
  isAuthenticated: boolean
  setUser: (u: User | null) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  isAuthenticated: false,
  setUser: (u) => set({ user: u, isAuthenticated: u !== null }),
  logout: () => set({ user: null, isAuthenticated: false }),
}))
